import PropTypes from 'prop-types';
import React, { useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { toPlural, toSeoUrl, fromSeoUrl } from 'helpers/HelperFunctions';
import { trackEvent } from 'helpers/Analytics';
import SingleLocationDropdown from 'components/SingleLocationDropdown/SingleLocationDropdown';
import Switch from 'components/Switch/Switch';
import AriAvatar from 'containers/Ari/AriAvatar';
import {
  getPopularJobCountriesShort,
  POPULAR_JOB_ROLES,
} from 'constants/popularJobLinks';
import {
  EXPERIENCE_LEVEL_OPTIONS,
  isExperienceGroupChecked,
} from './experienceLevelOptions';
import styles from './JobsFilterPanel.module.scss';

const INITIAL_VISIBLE_ROLES = 8;

const normalizeRoleKey = (value) =>
  (value || '').toString().toLowerCase().replace(/-/g, ' ').trim();

const roleHref = (roleName, slug) => {
  if (slug) {
    return `/jobs/${slug}`;
  }
  return `/jobs/${toSeoUrl(toPlural(roleName))}`;
};

const isRoleSelected = (selectedRoles, roleName, slug) => {
  if (!selectedRoles || selectedRoles.length === 0) return false;
  const keys = [
    normalizeRoleKey(roleName),
    normalizeRoleKey(slug),
    normalizeRoleKey(toPlural(roleName)),
  ].filter(Boolean);

  return selectedRoles.some((selected) =>
    keys.includes(normalizeRoleKey(selected))
  );
};

const resolveSelectedRole = (selected, suggestedRoles = []) => {
  const popular = POPULAR_JOB_ROLES.find((role) =>
    isRoleSelected([selected], role.name, role.slug)
  );
  if (popular) {
    return { name: popular.name, slug: popular.slug };
  }

  const suggested = (suggestedRoles || []).find((role) => {
    const name = role.name || role.label || role.value;
    const slug = role.slug_plural || role.slug || null;
    return isRoleSelected([selected], name, slug);
  });
  if (suggested) {
    return {
      name: suggested.name || suggested.label || suggested.value,
      slug: suggested.slug_plural || suggested.slug || null,
    };
  }

  return { name: selected, slug: null };
};

/**
 * Filter and order panel for the jobs board. Rendered twice by layout
 * (desktop column + mobile sheet) with the same props — never different content.
 */
const JobsFilterPanel = ({
  filter,
  suggestedRoles,
  featuredRoles,
  pageType,
  auth,
  filterCount,
  idPrefix,
  showPanelHeader,
  onRoleToggle,
  onExperienceGroupToggle,
  onHiringCapacityToggle,
  onRemoteFilterToggle,
  onInternalFilterToggle,
  onStatusFilterToggle,
  onLocationTextChange,
  onFiltersReset,
  onBestMatchNavigate,
}) => {
  const [rolesExpanded, setRolesExpanded] = useState(false);
  const sectionId = (suffix) =>
    idPrefix ? `jobs-filter-${idPrefix}-${suffix}` : `jobs-filter-${suffix}`;

  const roleOptions = useMemo(() => {
    const seen = new Set();
    const options = [];

    const pushRole = (name, slug) => {
      if (!name) return;
      const key = normalizeRoleKey(name);
      const slugKey = normalizeRoleKey(slug);
      if (seen.has(key) || (slugKey && seen.has(slugKey))) return;
      seen.add(key);
      if (slugKey) seen.add(slugKey);
      options.push({ name, slug: slug || null });
    };

    (filter.selectedRoles || []).forEach((selected) => {
      const resolved = resolveSelectedRole(selected, suggestedRoles);
      pushRole(resolved.name, resolved.slug);
    });

    (featuredRoles || []).forEach((name) => pushRole(name));

    POPULAR_JOB_ROLES.forEach((role) => pushRole(role.name, role.slug));

    (suggestedRoles || []).forEach((role) => {
      const name = role.name || role.label || role.value;
      const slug = role.slug_plural || role.slug || null;
      pushRole(name, slug);
    });

    return options;
  }, [featuredRoles, suggestedRoles, filter.selectedRoles]);

  const currentLocation =
    filter.locationText && fromSeoUrl(filter.locationText);

  const handleLocationReset = useCallback(() => {
    onLocationTextChange({ city: '', country: '', text: '' });
  }, [onLocationTextChange]);

  const handleBestMatch = (event) => {
    event.preventDefault();
    trackEvent('jobs-best-match-clicked', {
      source: 'panel',
      pageType,
      auth,
      filterCount,
    });
    if (onBestMatchNavigate) {
      onBestMatchNavigate();
    }
  };

  const clearAllDisabled = filterCount === 0;

  const clearAllButton = (
    <button
      type="button"
      className={styles.clearAll}
      onClick={onFiltersReset}
      disabled={clearAllDisabled}
      data-testid="jobs-filter-clear-all"
    >
      Clear all
    </button>
  );

  return (
    <div className={styles.panel} data-testid="jobs-filter-panel">
      {showPanelHeader && (
        <div className={styles.panelHeader}>
          <h2 className={styles.panelTitle}>Filter and order</h2>
          {clearAllButton}
        </div>
      )}

      <section className={styles.section} aria-labelledby={sectionId('order')}>
        <h3 id={sectionId('order')} className={styles.sectionTitle}>
          Order your results
        </h3>
        <div
          className={styles.orderList}
          role="radiogroup"
          aria-label="Order your results"
        >
          <div
            className={`${styles.orderOption} ${styles.orderOptionSelected}`}
            role="radio"
            aria-checked="true"
          >
            <span className={styles.radioOuter} aria-hidden="true">
              <span className={styles.radioInner} />
            </span>
            <span className={styles.orderContent}>
              <span className={styles.orderLabel}>Newest first</span>
              <span className={styles.orderHint}>
                Every job that fits, latest first.
              </span>
            </span>
          </div>
          <a
            href="/ari"
            className={styles.orderOption}
            role="radio"
            aria-checked="false"
            onClick={handleBestMatch}
            data-testid="jobs-best-match"
          >
            <span className={styles.radioOuter} aria-hidden="true" />
            <span className={styles.orderContent}>
              <span className={styles.bestMatchTitleRow}>
                <AriAvatar size={22} className={styles.ariAvatar} />
                <span className={styles.orderLabel}>Best match</span>
                {auth === 'guest' && (
                  <span className={styles.freeAccountTag}>Free account</span>
                )}
              </span>
              <span className={styles.orderHint}>
                Ari ranks jobs against your profile and explains each one.
              </span>
            </span>
          </a>
        </div>
      </section>

      <section className={styles.section} aria-labelledby={sectionId('roles')}>
        <h3 id={sectionId('roles')} className={styles.sectionTitle}>
          Roles
        </h3>
        <ul className={styles.checkList}>
          {roleOptions.map((role, index) => {
            const checked = isRoleSelected(
              filter.selectedRoles,
              role.name,
              role.slug
            );
            const href = roleHref(role.name, role.slug);
            const collapsedHidden =
              !rolesExpanded && index >= INITIAL_VISIBLE_ROLES && !checked;

            return (
              <li
                key={role.slug || role.name}
                className={`${styles.checkRow} ${
                  collapsedHidden ? styles.roleCollapsed : ''
                }`}
              >
                <button
                  type="button"
                  className={styles.checkButton}
                  onClick={() => onRoleToggle(role.name)}
                  aria-label={role.name}
                  aria-pressed={checked}
                  tabIndex={collapsedHidden ? -1 : undefined}
                >
                  <span
                    className={`${styles.checkbox} ${
                      checked ? styles.checkboxChecked : ''
                    }`}
                    aria-hidden="true"
                  >
                    {checked && <Check size={12} strokeWidth={3} />}
                  </span>
                </button>
                <a
                  href={href}
                  className={styles.roleLink}
                  tabIndex={collapsedHidden ? -1 : undefined}
                >
                  {role.name}
                </a>
              </li>
            );
          })}
        </ul>
        {roleOptions.length > INITIAL_VISIBLE_ROLES && !rolesExpanded && (
          <button
            type="button"
            className={styles.textButton}
            onClick={() => setRolesExpanded(true)}
          >
            More roles
          </button>
        )}
        <Link to="/jobs/categories" className={styles.textLink}>
          See every role
        </Link>
      </section>

      <section
        className={styles.section}
        aria-labelledby={sectionId('location')}
      >
        <h3 id={sectionId('location')} className={styles.sectionTitle}>
          Location
        </h3>
        <SingleLocationDropdown
          defaultLocation={currentLocation}
          shouldClearOnBlur={!filter.locationText}
          showResetIcon={!!filter.locationText}
          onLocationReset={handleLocationReset}
          onLocationChange={onLocationTextChange}
          placeholder="Any country or city"
          className={styles.locationDropdown}
        />
        <p className={styles.popularRow}>
          <span className={styles.popularLabel}>Popular:</span>{' '}
          {getPopularJobCountriesShort().map((country, index) => (
            <React.Fragment key={country.slug}>
              {index > 0 && (
                <span className={styles.popularSep} aria-hidden="true">
                  {' '}
                  ·{' '}
                </span>
              )}
              <a
                href={`/jobs/in/${country.slug}`}
                className={styles.popularLink}
              >
                {country.shortLabel}
              </a>
            </React.Fragment>
          ))}
        </p>
        <button
          type="button"
          className={styles.toggleRow}
          onClick={onRemoteFilterToggle}
        >
          <span className={styles.toggleLabel}>Remote</span>
          <Switch
            on={!!filter.remote}
            ariaLabel="Remote"
            className={styles.brandSwitch}
          />
        </button>
      </section>

      <section
        className={styles.section}
        aria-labelledby={sectionId('experience')}
      >
        <h3 id={sectionId('experience')} className={styles.sectionTitle}>
          Experience
        </h3>
        <ul className={styles.checkList}>
          {EXPERIENCE_LEVEL_OPTIONS.map((option) => {
            const checked = isExperienceGroupChecked(
              filter.experienceLevels,
              option
            );
            return (
              <li key={option.label} className={styles.checkRow}>
                <button
                  type="button"
                  className={styles.checkButton}
                  onClick={() => onExperienceGroupToggle(option)}
                  aria-pressed={checked}
                >
                  <span
                    className={`${styles.checkbox} ${
                      checked ? styles.checkboxChecked : ''
                    }`}
                    aria-hidden="true"
                  >
                    {checked && <Check size={12} strokeWidth={3} />}
                  </span>
                  <span className={styles.checkLabel}>{option.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      <section className={styles.section} aria-labelledby={sectionId('type')}>
        <h3 id={sectionId('type')} className={styles.sectionTitle}>
          Type
        </h3>
        <ul className={styles.checkList}>
          {(filter.hiringCapacities || [])
            .slice()
            .sort((a, b) => {
              // Commercial first, then Personal
              if (a.type === b.type) return 0;
              if (a.type === 'commercial') return -1;
              if (b.type === 'commercial') return 1;
              return 0;
            })
            .map((capacity) => (
              <li key={capacity.type} className={styles.checkRow}>
                <button
                  type="button"
                  className={styles.checkButton}
                  onClick={() => onHiringCapacityToggle(capacity)}
                  aria-pressed={!!capacity.isChecked}
                >
                  <span
                    className={`${styles.checkbox} ${
                      capacity.isChecked ? styles.checkboxChecked : ''
                    }`}
                    aria-hidden="true"
                  >
                    {capacity.isChecked && <Check size={12} strokeWidth={3} />}
                  </span>
                  <span className={styles.checkLabel}>{capacity.label}</span>
                </button>
              </li>
            ))}
        </ul>
      </section>

      <section className={styles.section}>
        <button
          type="button"
          className={styles.toggleRow}
          onClick={onInternalFilterToggle}
        >
          <span className={styles.toggleLabel}>Easy apply only</span>
          <Switch
            on={!!filter.internal}
            ariaLabel="Easy apply only"
            className={styles.brandSwitch}
          />
        </button>
        <button
          type="button"
          className={styles.toggleRow}
          onClick={onStatusFilterToggle}
        >
          <span className={styles.toggleLabel}>Open to applications</span>
          <Switch
            on={!!filter.status}
            ariaLabel="Open to applications"
            className={styles.brandSwitch}
          />
        </button>
      </section>
    </div>
  );
};

JobsFilterPanel.propTypes = {
  filter: PropTypes.object.isRequired,
  suggestedRoles: PropTypes.array,
  featuredRoles: PropTypes.arrayOf(PropTypes.string),
  pageType: PropTypes.oneOf(['jobs', 'role', 'country']).isRequired,
  auth: PropTypes.oneOf(['guest', 'logged_in']).isRequired,
  filterCount: PropTypes.number,
  /** Prefix for section heading ids when the panel is mounted twice (desktop + sheet). */
  idPrefix: PropTypes.string,
  /** Desktop column shows title + Clear all; sheet uses its own header. */
  showPanelHeader: PropTypes.bool,
  onRoleToggle: PropTypes.func.isRequired,
  onExperienceGroupToggle: PropTypes.func.isRequired,
  onHiringCapacityToggle: PropTypes.func.isRequired,
  onRemoteFilterToggle: PropTypes.func.isRequired,
  onInternalFilterToggle: PropTypes.func.isRequired,
  onStatusFilterToggle: PropTypes.func.isRequired,
  onLocationTextChange: PropTypes.func.isRequired,
  onFiltersReset: PropTypes.func.isRequired,
  onBestMatchNavigate: PropTypes.func,
};

JobsFilterPanel.defaultProps = {
  suggestedRoles: [],
  featuredRoles: [],
  filterCount: 0,
  idPrefix: '',
  showPanelHeader: true,
  onBestMatchNavigate: undefined,
};

export default JobsFilterPanel;
